<script lang="ts">
	/**
	 * Rooms header. In the new layout, search moves into the tabs kebab on
	 * mobile/tablet (same pattern as Devices / Scenes / Groups); desktop keeps
	 * the slim Toolbar search. Reorder stays in `$listHeader`. The old layout is
	 * preserved verbatim so nothing regresses outside the new-layout flag.
	 */
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Toolbar, Breadcrumbs, Input, BreadcrumbsFlexWrapper } from '@Shelly/shelly-design-system';
	import AddEditButtons from 'src/components/AddEditButtons.svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import { roomMainFilter } from 'src/stores/ui/filters';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { is_mobile, is_tablet } from 'src/stores';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	import { capFirstChar } from 'src/helpers/utilities';

	export let onShowAddEditModal: () => void = () => {};
	export let itemsLength = 0;

	const headerHandle = createListHeaderHandle();

	$: reorderDisabled = itemsLength <= 1;
	$: hideInlineSearch = $is_mobile || $is_tablet;

	// New layout: kebab in the tabs action slot while idle; primary "Finish
	// reordering" while editing. Search lives in the kebab on mobile/tablet.
	$: if ($showNewLayout) {
		const config: ListHeaderConfig = $edit
			? {
					primaryAction: {
						label: $format('label_finish_reordering', { default: 'Finish reordering' }),
						icon: 'fas fa-xmark',
						variant: 'secondary',
						onClick: () => edit.set(false),
					},
				}
			: {
					menuItems: [
						{
							items: [
								{
									title: $format('label_reorder_items', { default: 'Reorder items' }),
									icon: 'fa-solid fa-arrows-up-down-left-right',
									disabled: reorderDisabled,
									onClick: () => edit.set(true),
								},
							],
						},
					],
					search: hideInlineSearch
						? {
								value: $roomMainFilter,
								label: capFirstChar($format('search', { default: 'Search' })),
								id: 'rooms-kebab-search-input',
								onInput: (value) => roomMainFilter.set(value),
							}
						: null,
				};
		headerHandle.set(config);
	}

	onDestroy(() => {
		headerHandle.clear();
	});
</script>

{#if $showNewLayout}
	{#if !$edit && !hideInlineSearch}
		<Toolbar class="mt-m mb-m" isSticky useNewLayout noBreadcrumbs hideSlotRightSide>
			<svelte:fragment slot="leftSide">
				<Input
					class="text-capitalize-placeholder width-md-l"
					bind:value={$roomMainFilter}
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="rooms-toolbar-search-input"
					aria-labelledby="rooms-toolbar-search-input"
				/>
			</svelte:fragment>
		</Toolbar>
	{/if}
{:else}
	<Toolbar class="mb-m" isSticky useNewLayout hideSlotLeftSide={$edit} hideSlotRightSide={$is_mobile}>
		<BreadcrumbsFlexWrapper
			slot="breadcrumbs"
			gap="s"
			hideSlotRight={isActiveHomeShared($homes.list) || !$is_mobile}
		>
			<Breadcrumbs
				slot="left"
				class="flex-1 min-width-0 max-width-fit-content"
				listClasses="width-full"
				aria-label={$format('label_rooms', { default: 'Rooms' })}
				links={[{ label: capFirstChar($format('tab_rooms', { default: 'Rooms' })) }]}
			/>
			<AddEditButtons
				slot="right"
				on:add_click={async () => {
					onShowAddEditModal();
				}}
				disableEditButton={itemsLength <= 1}
				hideAddButton
				context="rooms"
			/>
		</BreadcrumbsFlexWrapper>
		<svelte:fragment slot="leftSide">
			<Input
				class="text-capitalize-placeholder width-md-l"
				bind:value={$roomMainFilter}
				type="text"
				label={capFirstChar($format('search', { default: 'Search' }))}
				icon="fas fa-search"
				id="rooms-toolbar-search-input"
				aria-labelledby="rooms-toolbar-search-input"
			/>
		</svelte:fragment>
		<svelte:fragment slot="rightSide">
			{#if !isActiveHomeShared($homes.list)}
				<AddEditButtons
					on:add_click={async () => {
						onShowAddEditModal();
					}}
					disableEditButton={itemsLength <= 1}
					context="rooms"
				/>
			{/if}
		</svelte:fragment>
	</Toolbar>
{/if}
